import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Lock,
  ShieldCheck,
  ShoppingCart,
} from "lucide-react";
import {
  defaultOrderConsentPayload,
  getMissingOrderConsentLabels,
} from "../order-consent";
import {
  confirmProjectOrder,
  fetchCustomerProject,
  fetchCustomerProjects,
  fetchProjectBilling,
  fetchProjectOrderSessionStatus,
  submitProjectOrder,
  updateProjectBillingSubscription,
  WorkflowApiError,
  type ProjectBillingOverview,
  type ProjectDetail,
  type ProjectSummary,
} from "../services/workflow";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getStripePaymentStatusLabel,
  isStripeCheckoutAccepted,
  isStripePaymentConfirmed,
} from "../services/workflow-ui";
import { useAuth } from "../contexts/AuthContext";

function getNextMonthlyBillingDate(referenceDate = new Date()) {
  return new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 1, 12, 0, 0);
}

function formatStripeMoney(amountInCents: number, currency = "eur") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100);
}

function getInvoiceStatusLabel(status: string | null) {
  switch (status) {
    case "paid":
      return "Bezahlt";
    case "open":
      return "Offen";
    case "draft":
      return "Entwurf";
    case "void":
      return "Storniert";
    case "uncollectible":
      return "Nicht einziehbar";
    default:
      return status || "Unbekannt";
  }
}

function getSubscriptionStatusLabel(status: string | null) {
  switch (status) {
    case "trialing":
      return "Testphase";
    case "active":
      return "Aktiv";
    case "past_due":
      return "Überfällig";
    case "unpaid":
      return "Unbezahlt";
    case "canceled":
      return "Beendet";
    case "incomplete":
      return "Unvollständig";
    case "incomplete_expired":
      return "Abgelaufen";
    case "paused":
      return "Pausiert";
    default:
      return status || "Unbekannt";
  }
}

function getMinimumContractEndDate(acceptedAt: string) {
  const acceptedDate = new Date(acceptedAt);
  if (Number.isNaN(acceptedDate.getTime())) {
    return null;
  }

  acceptedDate.setMonth(acceptedDate.getMonth() + 12);
  return acceptedDate;
}

function getLatestInvoiceWithPeriod(billing: ProjectBillingOverview | null) {
  if (!billing?.invoices?.length) {
    return null;
  }

  return [...billing.invoices].sort((left, right) => {
    const leftTime = new Date(left.periodEnd || left.createdAt || 0).getTime();
    const rightTime = new Date(right.periodEnd || right.createdAt || 0).getTime();
    return rightTime - leftTime;
  })[0];
}

export function ProjectCheckout() {
  const { user } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projectSummaries, setProjectSummaries] = useState<ProjectSummary[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isConfirmingCheckout, setIsConfirmingCheckout] = useState(false);
  const [billing, setBilling] = useState<ProjectBillingOverview | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);
  const [isUpdatingCancellation, setIsUpdatingCancellation] = useState(false);
  const [hasAutostarted, setHasAutostarted] = useState(false);
  const consents = defaultOrderConsentPayload;
  const isReadOnlyCustomerView = user?.impersonation?.active === true;
  const nextBillingDate = getNextMonthlyBillingDate();
  const checkoutSessionId = searchParams.get("session_id");
  const autostartCheckout = searchParams.get("autostart") === "1";

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        const summaries = await fetchCustomerProjects();

        if (!isMounted) {
          return;
        }

        setProjectSummaries(summaries);

        if (summaries.length === 0) {
          setSelectedProjectId("");
          setProject(null);
          setBilling(null);
          setIsLoading(false);
          return;
        }

        setSelectedProjectId((current) => {
          if (projectId) {
            return projectId;
          }

          if (current && summaries.some((summary) => summary.id === current)) {
            return current;
          }

          return summaries.find((summary) => summary.order)?.id || summaries[0]?.id || "";
        });
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError instanceof WorkflowApiError
            ? loadError.message
            : "Vertragsseite konnte nicht geladen werden",
        );
        setProject(null);
        setBilling(null);
        setSelectedProjectId("");
        setProjectSummaries([]);
        setIsLoading(false);
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!selectedProjectId) {
        setProject(null);
        setBilling(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const nextProject = await fetchCustomerProject(selectedProjectId);

        if (!isMounted) {
          return;
        }

        setProject(nextProject);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError instanceof WorkflowApiError
            ? loadError.message
            : "Projekt konnte nicht geladen werden",
        );
        setProject(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId || !checkoutSessionId || !project) {
      return;
    }

    let isMounted = true;
    setIsConfirmingCheckout(true);
    setError("");

    const finalizeReturn = async () => {
      try {
        const session = await fetchProjectOrderSessionStatus(selectedProjectId, checkoutSessionId);

        if (!isMounted) {
          return;
        }

        if (isStripeCheckoutAccepted(session.status, session.paymentStatus)) {
          const nextProject = await confirmProjectOrder(selectedProjectId, checkoutSessionId);

          if (!isMounted) {
            return;
          }

          setProject(nextProject);
          setSuccessMessage(
            isStripePaymentConfirmed(session.paymentStatus)
              ? "Zahlung erfolgreich. Ihr Auftrag wurde verbindlich übernommen."
              : "Bestellung übernommen. Die SEPA-Zahlung wird noch von Stripe verarbeitet.",
          );
        } else {
          setError("Der Checkout wurde noch nicht abgeschlossen. Sie können ihn unten erneut starten.");
        }
      } catch (checkoutError) {
        if (!isMounted) {
          return;
        }

        setError(
          checkoutError instanceof WorkflowApiError
            ? checkoutError.message
            : "Checkout-Rückkehr konnte nicht verarbeitet werden",
        );
      } finally {
        if (isMounted) {
          setIsConfirmingCheckout(false);
          navigate(`/dashboard/kauf/${selectedProjectId}`, { replace: true });
        }
      }
    };

    finalizeReturn();

    return () => {
      isMounted = false;
    };
  }, [checkoutSessionId, navigate, project, selectedProjectId]);

  useEffect(() => {
    let isMounted = true;

    const loadBilling = async () => {
      if (!selectedProjectId || !project?.order) {
        setBilling(null);
        setIsLoadingBilling(false);
        return;
      }

      setIsLoadingBilling(true);

      try {
        const nextBilling = await fetchProjectBilling(selectedProjectId);
        if (isMounted) {
          setBilling(nextBilling);
        }
      } catch (billingError) {
        if (!isMounted) {
          return;
        }

        setError(
          billingError instanceof WorkflowApiError
            ? billingError.message
            : "Abrechnungen konnten nicht geladen werden",
        );
      } finally {
        if (isMounted) {
          setIsLoadingBilling(false);
        }
      }
    };

    loadBilling();

    return () => {
      isMounted = false;
    };
  }, [project?.order?.id, selectedProjectId]);

  const handleStartCheckout = async () => {
    if (
      !selectedProjectId ||
      !project ||
      project.order ||
      isReadOnlyCustomerView ||
      isStartingCheckout ||
      isConfirmingCheckout
    ) {
      return;
    }

    setIsStartingCheckout(true);
    setError("");
    setSuccessMessage("");

    try {
      const missingConsents = getMissingOrderConsentLabels(consents);
      if (missingConsents.length > 0) {
        throw new WorkflowApiError(
          `Bitte bestätigen Sie vor dem Checkout noch: ${missingConsents.join(", ")}.`,
        );
      }

      const session = await submitProjectOrder(selectedProjectId, consents);

      if (!session.url) {
        throw new Error("Stripe hat keine Checkout-URL geliefert");
      }

      window.location.assign(session.url);
    } catch (checkoutError) {
      setError(
        checkoutError instanceof WorkflowApiError
          ? checkoutError.message
          : "Stripe-Checkout konnte nicht vorbereitet werden",
      );
      setIsStartingCheckout(false);
    }
  };

  useEffect(() => {
    if (
      !autostartCheckout ||
      hasAutostarted ||
      !project ||
      project.order ||
      isReadOnlyCustomerView ||
      isStartingCheckout ||
      isConfirmingCheckout ||
      !!checkoutSessionId
    ) {
      return;
    }

    setHasAutostarted(true);
    void handleStartCheckout();
  }, [
    autostartCheckout,
    checkoutSessionId,
    hasAutostarted,
    isReadOnlyCustomerView,
    isConfirmingCheckout,
    isStartingCheckout,
    project,
  ]);

  const minimumContractEnd = project?.order
    ? getMinimumContractEndDate(project.order.acceptedAt)
    : null;
  const subscriptionCurrentPeriodEnd = billing?.subscription?.currentPeriodEnd
    ? new Date(billing.subscription.currentPeriodEnd)
    : null;
  const scheduledCancellationDate =
    billing?.subscription?.cancelAt ||
    (billing?.subscription?.cancelAtPeriodEnd ? billing.subscription.currentPeriodEnd : null);
  const latestInvoiceWithPeriod = getLatestInvoiceWithPeriod(billing);
  const displayedCurrentPeriodEnd =
    billing?.subscription?.currentPeriodEnd ||
    latestInvoiceWithPeriod?.periodEnd ||
    null;
  const nextChargeDate =
    billing?.upcomingInvoice?.nextPaymentAttempt ||
    billing?.upcomingInvoice?.periodStart ||
    displayedCurrentPeriodEnd;
  const cancelsAtMinimumContractEnd = Boolean(
    billing?.subscription &&
      !scheduledCancellationDate &&
      minimumContractEnd &&
      subscriptionCurrentPeriodEnd &&
      !Number.isNaN(subscriptionCurrentPeriodEnd.getTime()) &&
      subscriptionCurrentPeriodEnd.getTime() < minimumContractEnd.getTime(),
  );

  const handleToggleCancellation = async (cancelAtPeriodEnd: boolean) => {
    if (
      !selectedProjectId ||
      !billing?.subscription ||
      isReadOnlyCustomerView ||
      isUpdatingCancellation
    ) {
      return;
    }

    const confirmed = window.confirm(
      cancelAtPeriodEnd
        ? minimumContractEnd && cancelsAtMinimumContractEnd
          ? `Soll die Subscription wirklich zum Laufzeitende am ${formatDate(minimumContractEnd.toISOString())} gekündigt werden?`
          : "Soll die Subscription wirklich zum Laufzeitende gekündigt werden?"
        : "Soll die vorgemerkte Kündigung wirklich zurückgenommen werden?",
    );

    if (!confirmed) {
      return;
    }

    setIsUpdatingCancellation(true);
    setError("");
    setSuccessMessage("");

    try {
      const nextBilling = await updateProjectBillingSubscription(selectedProjectId, {
        cancelAtPeriodEnd,
      });
      setBilling(nextBilling);
      setSuccessMessage(
        cancelAtPeriodEnd
          ? minimumContractEnd && cancelsAtMinimumContractEnd
            ? `Die Kündigung wurde zum Laufzeitende am ${formatDate(minimumContractEnd.toISOString())} vorgemerkt.`
            : "Die Kündigung wurde zum Laufzeitende vorgemerkt."
          : "Die vorgemerkte Kündigung wurde aufgehoben.",
      );
    } catch (billingError) {
      setError(
        billingError instanceof WorkflowApiError
          ? billingError.message
          : "Die Abo-Verwaltung konnte nicht aktualisiert werden",
      );
    } finally {
      setIsUpdatingCancellation(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-600">Kaufseite wird geladen...</div>;
  }

  if (!project) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error || "Projekt konnte nicht geladen werden"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            {project.order ? "Vertrag & Abrechnung" : "Kaufseite"}
          </p>
          <h1 className="mt-2 text-3xl font-semibold text-gray-950">
            {project.order ? "Bestellung und Abbuchungen" : "Direkt zu Stripe bestellen"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {project.order
              ? "Vertragsstatus, nächste Abbuchung und vergangene Rechnungen an einem Ort."
              : "Der Kaufabschluss erfolgt direkt über eine gehostete Stripe-Checkout-Seite."}
          </p>
        </div>

        {projectSummaries.length > 1 ? (
          <div className="flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
            <span>{projectSummaries.length} Projekte</span>
            <select
              value={selectedProjectId}
              onChange={(event) => {
                const nextProjectId = event.target.value;
                setSelectedProjectId(nextProjectId);

                if (projectId) {
                  navigate(`/dashboard/kauf/${nextProjectId}`);
                }
              }}
              className="max-w-[220px] truncate bg-transparent text-sm text-gray-950 focus:outline-none"
              aria-label="Projekt auswählen"
            >
              {projectSummaries.map((summary) => (
                <option key={summary.id} value={summary.id}>
                  {summary.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-100"
        >
          <ArrowLeft size={16} />
          Zurück ins Dashboard
        </Link>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {project.paymentIssue ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {project.paymentIssue.message}
          {project.paymentIssue.paymentStatus ? (
            <span className="mt-1 block font-semibold">
              Stripe-Status: {getStripePaymentStatusLabel(project.paymentIssue.paymentStatus)}
            </span>
          ) : null}
        </div>
      ) : null}

      {isReadOnlyCustomerView ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Checkout und Vertragsaktionen sind in der Admin-Kundenansicht deaktiviert.
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      ) : null}

      {isConfirmingCheckout ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Stripe-Zahlung wird geprüft und Ihr Auftrag wird übernommen...
        </div>
      ) : null}

      {isStartingCheckout ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Stripe-Checkout wird vorbereitet und direkt geöffnet...
        </div>
      ) : null}

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_360px]">
        <div className="space-y-6">
          {!project.order ? (
            <>
              <div className="rounded-3xl border border-gray-300 bg-white p-6 shadow-sm space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-800">
                      <ShoppingCart size={14} />
                      Hosted Checkout
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-950">Kaufübersicht</h2>
                      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-700">
                        Nach dem Klick öffnet sich das Stripe-Formular direkt auf einer separaten
                        Checkout-Seite. Die Vertragsdaten werden dabei automatisch mit Ihrem Projekt
                        verknüpft.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-300 bg-slate-50 px-4 py-3 text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                      Freigegebene Version
                    </p>
                    <p className="mt-2 text-lg font-semibold text-gray-950">
                      V{project.currentDraft?.version}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4 text-sm leading-relaxed text-gray-800">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                      Zahlungsmodell
                    </p>
                    <p className="mt-2">
                      Einmalige Umsetzung plus monatlicher Betrieb in einem gemeinsamen
                      Stripe-Checkout. Die finale Steuer wird dort passend zum Rechnungsland und
                      einer hinterlegten USt-ID berechnet.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4 text-sm leading-relaxed text-gray-800">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                      Vertragslaufzeit
                    </p>
                    <p className="mt-2">12 Monate Mindestlaufzeit, danach monatlich kündbar.</p>
                  </div>
                  <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4 text-sm leading-relaxed text-gray-800">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                      Abrechnung
                    </p>
                    <p className="mt-2">
                      Setup sofort, monatlicher Beitrag kostenlos bis{" "}
                      {formatDate(nextBillingDate.toISOString())}. Die finale Steuer wird im
                      Checkout berechnet.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-gray-300 bg-white p-6 shadow-sm space-y-5">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} className="text-gray-700" />
                  <h2 className="text-lg font-semibold text-gray-950">Stripe-Formular</h2>
                </div>

                <p className="text-sm leading-relaxed text-gray-700">
                  Im Checkout werden die Zahlungsseite und die Kundendaten direkt von Stripe erfasst.
                  Aktiviert sind dabei Rabattcodes, Telefonnummer, Rechnungsadresse,
                  Steuer-ID/USt-ID und Steuerberechnung.
                </p>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-relaxed text-amber-950">
                  Die finale Steuer wird im Stripe-Checkout anhand Rechnungsland sowie der
                  hinterlegten Steuer-ID/USt-ID berechnet und vor Abschluss separat ausgewiesen.
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm leading-relaxed text-blue-900">
                  {autostartCheckout
                    ? "Dieser Aufruf startet den Stripe-Checkout automatisch. Falls nichts passiert, können Sie ihn unten manuell erneut öffnen."
                    : "Mit dem Button unten verlassen Sie diese Seite und wechseln direkt auf das gehostete Stripe-Checkout-Formular."}
                </div>

                <div className="rounded-2xl border border-gray-200 bg-slate-50 px-4 py-4 text-sm leading-relaxed text-gray-800">
                  Unser Angebot ist ausschließlich auf B2B ausgelegt. Der Checkout startet ohne
                  zusätzliche Abfragen.
                </div>

                <button
                  type="button"
                  onClick={() => {
                    void handleStartCheckout();
                  }}
                  disabled={isReadOnlyCustomerView || isStartingCheckout || isConfirmingCheckout}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ExternalLink size={16} />
                  {isStartingCheckout ? "Formular wird vorbereitet..." : "Jetzt bestellen"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                className={`rounded-3xl p-6 shadow-sm ${
                  project.order.isPaymentConfirmed
                    ? "border border-green-200 bg-green-50"
                    : "border border-amber-200 bg-amber-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className={`mt-0.5 ${project.order.isPaymentConfirmed ? "text-green-700" : "text-amber-700"}`}
                    size={22}
                  />
                  <div>
                    <h2
                      className={`text-lg font-semibold ${
                        project.order.isPaymentConfirmed ? "text-green-900" : "text-amber-950"
                      }`}
                    >
                      {project.order.isPaymentConfirmed
                        ? "Bestellung abgeschlossen"
                        : "Bestellung übernommen"}
                    </h2>
                    <p
                      className={`mt-2 text-sm leading-relaxed ${
                        project.order.isPaymentConfirmed ? "text-green-800" : "text-amber-900"
                      }`}
                    >
                      Ihr Auftrag wurde am {formatDateTime(project.order.acceptedAt)} verbindlich
                      übernommen.
                      {!project.order.isPaymentConfirmed
                        ? " Die Zahlung wurde autorisiert, ist aber noch nicht endgültig auf unserem Konto eingegangen."
                        : ""}
                    </p>
                  </div>
                </div>
              </div>

              {!project.order.isPaymentConfirmed ? (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CreditCard className="mt-0.5 text-amber-700" size={22} />
                    <div>
                      <h2 className="text-lg font-semibold text-amber-950">Zahlung noch in Verarbeitung</h2>
                      <p className="mt-2 text-sm leading-relaxed text-amber-900">
                        Bei SEPA-Lastschrift kann die endgültige Bestätigung einige Tage dauern. Ihr
                        Projekt läuft bereits weiter, aber der Zahlungseingang ist noch nicht final
                        bestätigt.
                      </p>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-800">
                        Stripe-Status: {getStripePaymentStatusLabel(project.order.paymentStatus)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="rounded-3xl border border-gray-300 bg-white p-6 shadow-sm space-y-5">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} className="text-gray-700" />
                  <h2 className="text-lg font-semibold text-gray-950">Abrechnungen</h2>
                </div>

                {isLoadingBilling ? (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-slate-50 px-4 py-8 text-sm text-gray-600">
                    Abrechnungen werden geladen...
                  </div>
                ) : billing && billing.available ? (
                  <div className="space-y-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                          {billing.subscription?.cancelAtPeriodEnd
                            ? "Vertragsende"
                            : "Nächste Abbuchung"}
                        </p>
                        <p className="mt-2 text-2xl font-semibold text-gray-950">
                          {billing.subscription?.cancelAtPeriodEnd
                            ? billing.subscription.currentPeriodEnd
                              ? formatDate(billing.subscription.currentPeriodEnd)
                              : "-"
                            : billing.upcomingInvoice
                            ? formatStripeMoney(
                                billing.upcomingInvoice.total,
                                billing.upcomingInvoice.currency,
                              )
                            : "-"}
                        </p>
                        <p className="mt-2 text-sm text-gray-700">
                          {billing.subscription?.cancelAtPeriodEnd
                            ? billing.subscription.currentPeriodEnd
                              ? "Die Subscription endet zum Ende der laufenden Periode."
                              : "Die Kündigung ist vorgemerkt."
                            : nextChargeDate
                              ? `Voraussichtlich am ${formatDate(nextChargeDate)}`
                              : billing.upcomingInvoice?.periodEnd
                                ? `Abrechnungszeitraum bis ${formatDate(billing.upcomingInvoice.periodEnd)}`
                                : "Nächste Abbuchung aktuell nicht verfügbar"}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                          Zahlungsstatus
                        </p>
                        <p className="mt-2 text-base font-semibold text-gray-950">
                          {getStripePaymentStatusLabel(
                            billing?.paymentStatus || project.order?.paymentStatus || null,
                          )}
                        </p>
                        <p className="mt-2 text-sm text-gray-700">
                          {billing?.isPaymentConfirmed === false
                            ? "Die Zahlung ist autorisiert, aber noch nicht endgültig eingegangen."
                            : scheduledCancellationDate
                              ? `Kündigung vorgemerkt zum ${formatDate(scheduledCancellationDate)}`
                              : displayedCurrentPeriodEnd
                                ? `Aktuelle Periode bis ${formatDate(displayedCurrentPeriodEnd)}`
                                : "Keine Periodendaten verfügbar"}
                        </p>
                      </div>
                    </div>

                    {billing.subscription ? (
                      <div
                        className={`rounded-2xl border px-4 py-4 text-sm ${
                          scheduledCancellationDate
                            ? "border-amber-200 bg-amber-50 text-amber-950"
                            : "border-blue-200 bg-blue-50 text-blue-900"
                        }`}
                      >
                        <p className="font-semibold">Abo-Verwaltung</p>
                        <p className="mt-2 leading-relaxed">
                          {billing.subscription.status === "canceled"
                            ? "Die Subscription ist bereits beendet."
                            : scheduledCancellationDate
                              ? `Die Kündigung ist bereits vorgemerkt. Die Subscription endet zum ${formatDate(scheduledCancellationDate)}.`
                              : minimumContractEnd && cancelsAtMinimumContractEnd
                                ? `Sie können die Subscription hier zum Laufzeitende kündigen. Während der Mindestlaufzeit wird die Kündigung automatisch zum ${formatDate(minimumContractEnd.toISOString())} vorgemerkt.`
                                : "Sie können die Subscription hier zum Laufzeitende kündigen oder eine bereits vorgemerkte Kündigung wieder aufheben."}
                        </p>

                        {billing.subscription.status !== "canceled" ? (
                          <div className="mt-4">
                            {scheduledCancellationDate ? (
                              <button
                                type="button"
                                onClick={() => {
                                  void handleToggleCancellation(false);
                                }}
                                disabled={isReadOnlyCustomerView || isUpdatingCancellation}
                                className="inline-flex items-center justify-center rounded-2xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-semibold text-amber-950 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isUpdatingCancellation
                                  ? "Wird aktualisiert..."
                                  : "Kündigung zurücknehmen"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  void handleToggleCancellation(true);
                                }}
                                disabled={isReadOnlyCustomerView || isUpdatingCancellation}
                                className="inline-flex items-center justify-center rounded-2xl bg-black px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isUpdatingCancellation
                                  ? "Wird aktualisiert..."
                                  : "Zum Laufzeitende kündigen"}
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="rounded-2xl border border-gray-300 bg-white">
                      <div className="border-b border-gray-300 px-4 py-3">
                        <h3 className="font-semibold text-gray-950">Vergangene Rechnungen</h3>
                      </div>

                      {billing.invoices.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {billing.invoices.map((invoice) => (
                            <div
                              key={invoice.id}
                              className="flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="font-medium text-gray-950">
                                  {invoice.number || "Rechnung"}
                                </p>
                                <p className="mt-1 text-sm text-gray-700">
                                  {invoice.createdAt ? formatDateTime(invoice.createdAt) : "-"}
                                </p>
                                {invoice.description ? (
                                  <p className="mt-1 text-sm text-gray-600">{invoice.description}</p>
                                ) : null}
                              </div>

                              <div className="flex flex-col items-start gap-3 md:items-end">
                                <div className="text-left md:text-right">
                                  <p className="font-semibold text-gray-950">
                                    {formatStripeMoney(invoice.total, invoice.currency)}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {getInvoiceStatusLabel(invoice.status)}
                                  </p>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  {invoice.hostedInvoiceUrl ? (
                                    <a
                                      href={invoice.hostedInvoiceUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
                                    >
                                      Öffnen
                                    </a>
                                  ) : null}
                                  {invoice.invoicePdf ? (
                                    <a
                                      href={invoice.invoicePdf}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
                                    >
                                      PDF
                                    </a>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-8 text-sm text-gray-600">
                          Noch keine vergangenen Abbuchungen vorhanden.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                    {billing?.reason || "Für diese Bestellung sind aktuell keine Abrechnungsdaten verfügbar."}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-3xl border border-gray-300 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                  Ihre Auswahl
                </p>
                <h2 className="mt-2 text-xl font-semibold text-gray-950">{project.title}</h2>
                <p className="mt-1 text-sm text-gray-700">
                  Umsetzung des freigegebenen Entwurfs V{project.currentDraft?.version}
                </p>
              </div>
              <Lock size={18} className="text-gray-500" />
            </div>

            <div className="mt-5 space-y-4 border-t border-gray-300 pt-4">
              <div className="flex items-start justify-between gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-900">Setup & Umsetzung</p>
                  <p className="text-gray-600">Heute fällig</p>
                </div>
                <p className="font-semibold text-gray-950">{formatCurrency(project.pricing.setup)}</p>
              </div>

              <div className="flex items-start justify-between gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-900">Monatlicher Betrieb</p>
                  <p className="text-gray-600">
                    Kostenlos bis {formatDate(nextBillingDate.toISOString())}
                  </p>
                </div>
                <p className="font-semibold text-gray-950">{formatCurrency(project.pricing.monthly)}</p>
              </div>

              <p className="text-xs leading-relaxed text-gray-500">
                Die endgültige Steuerberechnung erfolgt im Stripe-Checkout passend zum Rechnungsland
                und einer hinterlegten USt-ID bzw. Steuer-ID.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-300 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <CalendarDays size={18} className="text-gray-700" />
              <h2 className="font-semibold text-gray-950">Wichtige Konditionen</h2>
            </div>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-gray-700">
              <li>Einmaliger Startpreis: {formatCurrency(project.pricing.setup)} vor Steuern</li>
              <li>Monatlicher Beitrag: {formatCurrency(project.pricing.monthly)} vor Steuern</li>
              <li>Mindestlaufzeit: 12 Monate</li>
              <li>Danach monatlich kündbar</li>
            </ul>
          </div>

          {!project.order ? (
            <div className="rounded-3xl border border-gray-300 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-gray-700" />
                <h2 className="font-semibold text-gray-950">Checkout-Daten</h2>
              </div>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-gray-700">
                <li>E-Mail wird aus Ihrem Kundenkonto vorbelegt</li>
                <li>Telefon und Rechnungsadresse werden bei Stripe abgefragt</li>
                <li>Steuer-ID/USt-ID, Rabattcodes und Steuerberechnung sind aktiviert</li>
                <li>AGB und Datenschutz sind verlinkt; kein Widerruf, da ausschließlich B2B</li>
              </ul>
            </div>
          ) : null}
        </aside>
      </section>
    </div>
  );
}
